import React, { ReactNode } from 'react';
import { GoogleTagManager } from '@next/third-parties/google';
import type { GTMParams } from 'node_modules/@next/third-parties/dist/types/google';
import { env } from '~/app/environment';
import { useLoginUser } from '~/common/providers/auth/use-login-user';
import { useTenant } from '~/common/hooks/use-tenant';
import { useEnabledFeatures } from '~/common/hooks/use-enabled-feature-map';

type GoogleTagManagerProviderProps = {
  children?: ReactNode;
};

const GoogleTagManagerProvider = React.memo(function GoogleTagManagerProvider({
  children,
}: GoogleTagManagerProviderProps): React.JSX.Element {
  const loginUser = useLoginUser();
  const { data: tenant } = useTenant();
  const { data: enabledFeatures } = useEnabledFeatures();
  const environment = env();
  const gtmParams = () => {
    const environmentParams =
      environment?.googleTagManager.gtm_auth && environment?.googleTagManager.gtm_preview
        ? ({
            auth: environment?.googleTagManager.gtm_auth,
            preview: environment?.googleTagManager.gtm_preview,
          } as const satisfies Pick<GTMParams, 'auth' | 'preview'>)
        : undefined;

    return {
      gtmId: environment?.googleTagManager.gtm_id ?? '',
      dataLayer: {
        userId: loginUser?.userInfo?.userId ?? '',
        // FIXME: この時点で useTenant を使用したテナント情報の取得が完了していない可能性があるので auth0 経由の tenantInfo を使う。
        // 本当はここに到達する時点で必ず tenantId, tenantName が参照できる状態にしたい
        tenantId: loginUser?.tenantInfo?.tenantId ?? '',
        tenantName: tenant?.name ?? '',
        emailAddress: loginUser?.email ?? '',
        firstName: loginUser?.userInfo?.firstName ?? '',
        lastName: loginUser?.userInfo?.lastName ?? '',
        role: loginUser?.role ?? '',
        enabledFeatures: enabledFeatures ?? [],
      },
      ...environmentParams,
    } satisfies GTMParams;
  };

  return (
    <>
      <GoogleTagManager {...gtmParams()} />
      {children}
    </>
  );
});

export { GoogleTagManagerProvider };
